import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import notificar, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuEye, LuTrash2, LuCopy, LuChartLine, LuList, LuLayoutGrid,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import { FiltroPill, ListCard, ListCards, ListRow, RowAction, RowActions } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import MiniaturaPainel from '../components/MiniaturaPainel';
import RelatorioPersonalizadoService, { type RelatorioSalvo } from '../services/RelatorioPersonalizadoService';
import './Relatorios.css';

/* ─── Regras de tela ──────────────────────────────────────────────

   A coluna de ações é olho + lixeira, e não lápis: aqui o verbo da linha é
   LER o resultado — é para isso que um relatório existe — e isso vale igual
   para o pronto da plataforma e para o que eu mesmo montei. Editar e duplicar
   vivem dentro do visualizador, onde a pessoa já está vendo o que vai mudar;
   é a mesma razão pela qual o Editar de um painel com abas mora dentro da aba
   (padrao-de-tela.md, "Editar em painel com abas").

   A lixeira só aparece onde a remoção existe: pronto da plataforma não se
   remove, e o servidor recusa. Botão que não faz nada é pior que botão
   ausente.                                                                  */

const rotuloFonte = (id: string): string =>
    ({ negocios: 'Negócios', contatos: 'Contatos', ligacoes: 'Ligações', metas: 'Metas' } as Record<string, string>)[id] ?? id;

const dataCurta = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';

    const hoje = new Date();
    const mesmoDia = d.toDateString() === hoje.toDateString();
    if (mesmoDia) return `hoje, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;

    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (d.toDateString() === ontem.toDateString()) return 'ontem';

    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/* A coluna de ações tem 106px, e não os 78px do padrão: com duplicar vindo do
   visualizador são TRÊS ícones de 26px, e em 78px o terceiro transbordava por
   cima da data. O padrão de 78px foi desenhado para dois. */
const COLUNAS = '1.5fr .7fr .9fr .7fr .7fr 106px';

/* O modo de exibição fica gravado no navegador de cada pessoa, e não zerado a
   cada visita: é a mesma regra do filtro que não se perde ao voltar. Quem
   escolheu blocos escolheu porque prefere ver a cara dos relatórios. */
type Visao = 'lista' | 'blocos';
const CHAVE_VISAO = 'relatorios:visao';

const lerVisao = (): Visao => {
    try {
        return localStorage.getItem(CHAVE_VISAO) === 'blocos' ? 'blocos' : 'lista';
    } catch {
        return 'lista';   // janela anônima, armazenamento bloqueado
    }
};

export default function RelatoriosPage() {
    const navegar = useNavigate();
    const celular = useCelular();

    const [relatorios, setRelatorios] = useState<RelatorioSalvo[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    /* Esta tela SAI para o painel do relatorio e volta. (A `visao` mais abaixo
       e preferencia de leitura e continua no localStorage.) */
    const [recorte, gravarRecorte] = useRecorte('rel:recorte', {
        busca: '', fonte: null as string | null, origem: null as string | null,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [fonte, setFonte] = useState<string | null>(recorte.fonte);
    const [origem, setOrigem] = useState<string | null>(recorte.origem);
    useEffect(() => {
        gravarRecorte({ busca, fonte, origem });
    }, [busca, fonte, origem, gravarRecorte]);
    const [confirmando, setConfirmando] = useState<number | null>(null);
    const [duplicando, setDuplicando] = useState<number | null>(null);
    const [visao, setVisao] = useState<Visao>(lerVisao);

    const trocarVisao = (v: Visao) => {
        setVisao(v);
        try { localStorage.setItem(CHAVE_VISAO, v); } catch { /* sem armazenamento, vale só nesta visita */ }
    };

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            setRelatorios(await RelatorioPersonalizadoService.listar());
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível carregar os relatórios.');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    /** Quem fez: é o que separa o pronto da plataforma do que a equipe montou. */
    const origemDe = (r: RelatorioSalvo): string =>
        r.sistema ? 'Pronto da plataforma' : r.podeEditar ? 'Meu' : 'Da empresa';

    const opcoesFonte = useMemo(() => {
        const mapa = new Map<string, number>();
        relatorios.forEach(r => mapa.set(rotuloFonte(r.fonte), (mapa.get(rotuloFonte(r.fonte)) ?? 0) + 1));
        return [...mapa].map(([valor, total]) => ({ valor, total }));
    }, [relatorios]);

    const opcoesOrigem = useMemo(() => {
        const mapa = new Map<string, number>();
        relatorios.forEach(r => mapa.set(origemDe(r), (mapa.get(origemDe(r)) ?? 0) + 1));
        return [...mapa].map(([valor, total]) => ({ valor, total }));
    }, [relatorios]);

    const filtrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return relatorios.filter(r => {
            if (fonte && rotuloFonte(r.fonte) !== fonte) return false;
            if (origem && origemDe(r) !== origem) return false;
            if (!termo) return true;
            return r.nome.toLowerCase().includes(termo)
                || (r.descricao ?? '').toLowerCase().includes(termo);
        });
    }, [relatorios, busca, fonte, origem]);

    /* Duplicar mora aqui desde 18/09, a pedido dele — saiu do cabeçalho do
       visualizador. A cópia abre direto no editor: quem duplica quer mudar
       alguma coisa, senão abriria o original. */
    const duplicar = async (r: RelatorioSalvo) => {
        setDuplicando(r.relatorioSalvoId);
        try {
            const copia = await RelatorioPersonalizadoService.duplicar(r.relatorioSalvoId);
            notificar.ok(`"${r.nome}" foi duplicado como seu relatório.`);
            navegar(`/relatorios/${copia.relatorioSalvoId}/editar`);
        } catch (e) {
            notificar.erro(textoDoErro(e, 'Não foi possível duplicar.'));
        } finally {
            setDuplicando(null);
        }
    };

    const remover = async (r: RelatorioSalvo) => {
        try {
            await RelatorioPersonalizadoService.remover(r.relatorioSalvoId);
            notificar.ok(`"${r.nome}" foi removido.`);
            setConfirmando(null);
            await carregar();
        } catch (e) {
            notificar.erro(textoDoErro(e, 'Não foi possível remover.'));
        }
    };

    const temFiltro = !!(busca || fonte || origem);

    /** O que a tela diz quando não há o que listar — igual nos dois modos. */
    const aviso = carregando ? null : erro ? (
        <div className="lk-blank">
            <p className="lk-blank-title">Não foi possível carregar</p>
            <p className="lk-blank-text">{erro}</p>
            <button type="button" className="lk-btn" onClick={() => void carregar()}>Tentar de novo</button>
        </div>
    ) : filtrados.length === 0 ? (
        <div className="lk-blank">
            <p className="lk-blank-title">
                {temFiltro ? 'Nenhum relatório no filtro' : 'Nenhum relatório ainda'}
            </p>
            <p className="lk-blank-text">
                {temFiltro
                    ? 'Nenhum relatório casa com a busca e os filtros escolhidos.'
                    : 'Monte o primeiro a partir de uma fonte de dados, ou duplique um pronto da plataforma.'}
            </p>
            {temFiltro
                ? <button type="button" className="lk-btn"
                    onClick={() => { setBusca(''); setFonte(null); setOrigem(null); }}>
                    Limpar filtros
                </button>
                : <button type="button" className="lk-btn is-primary" onClick={() => navegar('/relatorios/novo')}>
                    <LuChartLine size={14} /> Montar um relatório
                </button>}
        </div>
    ) : null;

    return (
        <div className="page-shell lk-screen rl-screen">
            <PageHeader
                title="Relatórios"
                subtitle="Os prontos da plataforma e os que a sua equipe montou"
                actions={
                    <div className="lk-actions">
                        {/* Mesma forma do alternador de Templates e Anotações: moldura
                            única, 32px, estado persistente — não é hover. */}
                        <div className="rl-vis" role="group" aria-label="Formato da lista">
                            <button type="button" className={visao === 'lista' ? 'is-on' : ''}
                                title="Lista" aria-pressed={visao === 'lista'}
                                onClick={() => trocarVisao('lista')}>
                                <LuList size={14} />
                            </button>
                            <button type="button" className={visao === 'blocos' ? 'is-on' : ''}
                                title="Blocos" aria-pressed={visao === 'blocos'}
                                onClick={() => trocarVisao('blocos')}>
                                <LuLayoutGrid size={14} />
                            </button>
                        </div>
                        {/* Sem relatório nenhum, a porta é a do aviso, no meio da tela. */}
                        {!(!carregando && !erro && !temFiltro && filtrados.length === 0) && (
                            <button type="button" className="lk-btn is-primary" onClick={() => navegar('/relatorios/novo')}>
                                <LuPlus size={14} /> Novo relatório
                            </button>
                        )}
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input
                            type="search"
                            placeholder="Buscar relatório…"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                        />
                    </label>

                    <FiltroPill rotulo="Fonte" valor={fonte} opcoes={opcoesFonte} onChange={setFonte} />
                    <FiltroPill rotulo="Origem" valor={origem} opcoes={opcoesOrigem} onChange={setOrigem} />

                    <span className="lk-count">
                        {relatorios.length} {relatorios.length === 1 ? 'relatório' : 'relatórios'}
                        {filtrados.length !== relatorios.length && ` · ${filtrados.length} no filtro`}
                    </span>
                </div>

                {/* ─── Blocos: a miniatura do painel é a razão deste modo existir.
                    Sem ela seriam os mesmos dados da lista, em caixas maiores. ─── */}
                {visao === 'blocos' && !carregando && filtrados.length > 0 && (
                    <div className="rl-cards">
                        {filtrados.map(r => (
                            <article key={r.relatorioSalvoId} className="rl-card">
                                <MiniaturaPainel arranjo={r.arranjo} />

                                <div className="rl-card-txt">
                                    <p className="rl-card-n">
                                        {r.nome}
                                        {r.totalBlocos > 1 && <span className="rl-blocos">{r.totalBlocos} blocos</span>}
                                    </p>
                                    {r.descricao && <p className="rl-card-d">{r.descricao}</p>}
                                </div>

                                <div className="rl-card-pe">
                                    <span className={`rl-tag ${r.sistema ? 'is-sys' : r.podeEditar ? 'is-meu' : 'is-emp'}`}>
                                        {origemDe(r)}
                                    </span>
                                    <span className="rl-card-f">{rotuloFonte(r.fonte)}</span>
                                    {/* As mesmas três ações da linha, no mesmo verbo e na mesma ordem.
                                        O cartão em si não é clicável: clicar na linha não abre nada nas
                                        outras doze telas, e um cartão que abre seria uma porta escondida. */}
                                    <RowActions>
                                        <RowAction
                                            icon={<LuEye size={14} />}
                                            label={`Abrir ${r.nome}`}
                                            onClick={() => navegar(`/relatorios/${r.relatorioSalvoId}`)}
                                        />
                                        <RowAction
                                            icon={<LuCopy size={14} />}
                                            label={`Duplicar ${r.nome}`}
                                            disabled={duplicando === r.relatorioSalvoId}
                                            onClick={() => void duplicar(r)}
                                        />
                                        {r.podeEditar && (
                                            <RowAction
                                                icon={<LuTrash2 size={14} />}
                                                label={`Excluir ${r.nome}`}
                                                danger
                                                onClick={() => setConfirmando(r.relatorioSalvoId)}
                                            />
                                        )}
                                    </RowActions>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                {visao === 'blocos' && carregando && (
                    <div className="rl-cards">
                        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="rl-card is-skel" />)}
                    </div>
                )}

                {/* Celular: a grade de seis colunas não cabe em 358px, então a linha
                    vira cartão — nome e descrição, com fonte, origem e a contagem de
                    blocos como estado. Visibilidade e data ficam no relatório. */}
                {visao === 'lista' && celular ? (
                    <>
                        <ListCards>
                            {carregando
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : filtrados.map(r => (
                                    <ListCard
                                        key={r.relatorioSalvoId}
                                        titulo={r.nome}
                                        apoio={r.descricao || rotuloFonte(r.fonte)}
                                        estado={
                                            <>
                                                <span className={`rl-tag ${r.sistema ? 'is-sys' : r.podeEditar ? 'is-meu' : 'is-emp'}`}>
                                                    {origemDe(r)}
                                                </span>
                                                <span>· {rotuloFonte(r.fonte)}</span>
                                                {/* Painel de vários blocos se anuncia aqui pela mesma razão
                                                    da linha: abrir para descobrir que são sete perguntas é caro. */}
                                                {r.totalBlocos > 1 && <span>· {r.totalBlocos} blocos</span>}
                                            </>
                                        }
                                        label={`Abrir ${r.nome}`}
                                        onOpen={() => navegar(`/relatorios/${r.relatorioSalvoId}`)}
                                        acoes={r.podeEditar && (
                                            <RowAction
                                                icon={<LuTrash2 size={15} />}
                                                label={`Excluir ${r.nome}`}
                                                danger
                                                onClick={() => setConfirmando(r.relatorioSalvoId)}
                                            />
                                        )}
                                    />
                                ))}
                        </ListCards>
                        {aviso}
                    </>
                ) : visao === 'lista' && (
                /* Renderizado só no modo lista, e não escondido com `hidden`: o
                   atributo perde para o `display: flex` que a classe declara, e a
                   tabela continuaria na tela embaixo dos cartões. */
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Relatório</div>
                            <div className="lk-head">Fonte</div>
                            <div className="lk-head">Origem</div>
                            <div className="lk-head">Visibilidade</div>
                            <div className="lk-head">Atualizado</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>

                        <div className="lk-body">
                            {carregando
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : filtrados.map(r => (
                                    <ListRow key={r.relatorioSalvoId} columns={COLUNAS}>
                                        <span className="lk-cell">
                                            <span className="rl-nome">
                                                <span className="lk-strong">
                                                    {r.nome}
                                                    {/* Painel de vários blocos se anuncia na linha: abrir para
                                                        descobrir que são sete perguntas é caro, e a contagem é
                                                        a única diferença estrutural entre uma linha e outra. */}
                                                    {r.totalBlocos > 1 && (
                                                        <span className="rl-blocos">{r.totalBlocos} blocos</span>
                                                    )}
                                                </span>
                                                {r.descricao && <span className="rl-desc">{r.descricao}</span>}
                                            </span>
                                        </span>
                                        <span className="lk-cell lk-muted">{rotuloFonte(r.fonte)}</span>
                                        <span className="lk-cell">
                                            <span className={`rl-tag ${r.sistema ? 'is-sys' : r.podeEditar ? 'is-meu' : 'is-emp'}`}>
                                                {origemDe(r)}
                                            </span>
                                        </span>
                                        <span className="lk-cell lk-muted">
                                            {r.visibilidade === 'empresa' ? 'Empresa' : 'Só eu'}
                                        </span>
                                        <span className="lk-cell lk-muted">
                                            {r.sistema ? '—' : dataCurta(r.atualizadoEm)}
                                        </span>
                                        <RowActions>
                                            <RowAction
                                                icon={<LuEye size={14} />}
                                                label={`Abrir ${r.nome}`}
                                                onClick={() => navegar(`/relatorios/${r.relatorioSalvoId}`)}
                                            />
                                            <RowAction
                                                icon={<LuCopy size={14} />}
                                                label={`Duplicar ${r.nome}`}
                                                disabled={duplicando === r.relatorioSalvoId}
                                                onClick={() => void duplicar(r)}
                                            />
                                            {r.podeEditar && (
                                                <RowAction
                                                    icon={<LuTrash2 size={14} />}
                                                    label={`Excluir ${r.nome}`}
                                                    danger
                                                    onClick={() => setConfirmando(r.relatorioSalvoId)}
                                                />
                                            )}
                                        </RowActions>
                                    </ListRow>
                                ))}
                        </div>
                    </div>

                    {aviso}
                </div>
                )}

                {/* Erro e vazio valem para os dois modos, e o mesmo JSX serve aos
                    dois — no de blocos ele fica solto, porque ali não há moldura
                    de tabela para segurá-lo. */}
                {visao === 'blocos' && aviso}
            </div>

            {confirmando !== null && (() => {
                const alvo = relatorios.find(r => r.relatorioSalvoId === confirmando);
                if (!alvo) return null;
                return (
                    <div className="rl-confirm-bar" role="alert">
                        <span className="rl-confirm-q">
                            Excluir <b>{alvo.nome}</b>? O relatório sai da galeria de quem o vê.
                        </span>
                        <button type="button" className="lk-btn" onClick={() => setConfirmando(null)}>Não</button>
                        <button type="button" className="lk-btn is-danger" onClick={() => void remover(alvo)}>
                            <LuTrash2 size={14} /> Excluir
                        </button>
                    </div>
                );
            })()}
        </div>
    );
}
